import {
  ToastType,
  TOAST_IDS,
  PermissionsEnum,
  UserStatus,
  PermissionLevel,
} from "@/modules/common/types";
import {
  getErrorMessage,
  showToast,
  userHasPermission,
} from "@/modules/common/utils";
import { queryClient } from "@/queryClient";
import { useContext, useState } from "react";
import { assignTrackToUser } from "@/modules/property-tracks/api";
import useUsersData from "@/modules/common/hooks/fetch-users-data.hook";
import { NawyPassportContext } from "@/modules/common/context/nawy-passport.provider";
import { Track } from "../types";
import { useOrganization } from "@/modules/common/context/organizations/get-user-organizations.provider";
import { useDomainMutation } from "@/modules/common/hooks/domain-mutation.hook";

interface UseEditTrackAssignedUserProps {
  track?: Track;
}

const useEditTrackAssignedUser = ({
  track,
}: UseEditTrackAssignedUserProps = {}) => {
  const { permissions: currentUserPermissions } =
    useContext(NawyPassportContext);
  const { selectedOrganization } = useOrganization();

  const [editAssignedUserModalOpen, setEditAssignedUserModalOpen] =
    useState(false);

  const CanAssignNonAvailableUsers = userHasPermission(
    {
      name: PermissionsEnum.CAN_ASSIGN_NON_AVAILABLE_USERS_JOURNEY,
      level: PermissionLevel.WRITE,
    },
    currentUserPermissions
  );

  const statuses = [UserStatus.Active, UserStatus.Fresh];

  const { assignedUsers, usersLoading, setUsersSearchTerm, usersSearchTerm } =
    useUsersData({
      initialParams: {
        ...(CanAssignNonAvailableUsers ? {} : { isActiveOnly: true }),
        assignToUsersQuery: true,
        statuses: statuses,
      },
      enabled: !!editAssignedUserModalOpen,
      organizationUId: selectedOrganization?.uid,
    });

  const [assignedUserId, setAssignedUserId] = useState<string | undefined>(
    track?.user?.uid
  );

  const handleChangeAssignedUser = (value: string) => {
    setAssignedUserId(value);
  };

  const {
    mutateAsync: assignTrackToUserMutation,
    isLoading,
    isError,
    isSuccess,
    error,
  } = useDomainMutation({
    mutationFn: ({
      trackUId,
      userUId,
    }: {
      trackUId: string | undefined;
      userUId: string;
    }) => assignTrackToUser({ trackUId: trackUId!, userUId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["track"] });
      queryClient.invalidateQueries({ queryKey: ["tracks"] });
      queryClient.invalidateQueries({ queryKey: ["track-activity"] });

      setEditAssignedUserModalOpen(false);

      showToast({
        type: ToastType.SUCCESS,
        message: "Track assigned successfully",
        toastId: TOAST_IDS.EDIT_LEAD_SUCCESS,
        options: {
          position: "bottom-left",
          autoClose: 3000,
        },
      });
    },
    onError: (error: {
      response: { data: { message: string; statusCode: number } };
    }) => {
      showToast({
        type: ToastType.ERROR,
        message: getErrorMessage(error) || "Error assigning track",
        toastId: TOAST_IDS.EDIT_LEAD_ERROR,
        options: {
          position: "bottom-left",
          autoClose: 3000,
        },
      });
    },
  });

  const handleAssignTrack = async () => {
    if (assignedUserId && track?.uid) {
      await assignTrackToUserMutation({
        trackUId: track.uid,
        userUId: assignedUserId,
      });
    }
  };

  const toggleEditAssignedUserModal = (isOpen: boolean) => {
    setEditAssignedUserModalOpen(isOpen);
  };

  return {
    assignedUser: {
      usersSearchTerm,
      setUsersSearchTerm,
      usersLoading,
      editAssignedUserModalOpen,
      setEditAssignedUserModalOpen,
      handleChangeAssignedUser,
      handleAssignTrack,
      isLoading,
      isError,
      isSuccess,
      error,
      value: track?.user?.uid,
      state: assignedUserId,
      data: assignedUsers,
    },
    toggleEditAssignedUserModal,
  };
};

export default useEditTrackAssignedUser;
